import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Star, TrendingUp, AlertCircle, CheckCircle2, Send, MessageSquare } from "lucide-react";
import {
  HeroCard, StatCard, ChartCard, InsightCard, Toolbar, SkeletonCard, EmptyState, RankingCard
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-slate-400 font-medium">{payload[0]?.payload?.date || payload[0]?.payload?.name || ''}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold text-white">
          {p.name}: {typeof p.value === "number" ? p.value.toFixed(0) : p.value}
        </p>
      ))}
    </div>
  );
};

export function EvaluacionesTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const [revRes, statsRes] = await Promise.all([
        authAxiosRef.current.get("/reviews"),
        authAxiosRef.current.get("/reviews/stats"),
      ]);
      setReviews(revRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Erro ao carregar avaliações");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);

  const handleReply = async (reviewId) => {
    if (!replyText.trim()) return;
    try {
      await authAxios.patch(`/reviews/${reviewId}/reply`, { reply: replyText });
      toast.success("Respuesta enviada");
      setReplyingId(null);
      setReplyText("");
      fetchData();
    } catch { toast.error("Error al responder"); }
  };

  const toggleVisibility = async (reviewId, hidden) => {
    try {
      await authAxios.patch(`/reviews/${reviewId}/visibility`, { hidden });
      toast.success(hidden ? "Evaluacion ocultada" : "Evaluacion visible");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const filtered = reviews.filter(r => {
    if (filter === "all") return true;
    if (filter === "hidden") return r.hidden;
    if (filter === "no-reply") return !r.admin_reply && !r.hidden;
    return r.rating === parseInt(filter);
  });

  if (loading) return <SkeletonCard count={4} type="stat" />;

  if (!stats) {
    return <EmptyState title="Sem avaliações" message="Nenhuma avaliação disponível" />;
  }

  const pct5Stars = stats.count > 0 ? ((stats.r5 || 0) / stats.count) * 100 : 0;
  const pctNoReply = stats.count > 0 ? ((stats.count - (stats.replied || 0)) / stats.count) * 100 : 0;

  const timelineData = [
    { date: 'D1', reviews: Math.floor(Math.random() * 5) + 2 },
    { date: 'D2', reviews: Math.floor(Math.random() * 5) + 3 },
    { date: 'D3', reviews: Math.floor(Math.random() * 5) + 1 },
    { date: 'D4', reviews: Math.floor(Math.random() * 5) + 4 },
    { date: 'D5', reviews: Math.floor(Math.random() * 5) + 2 },
    { date: 'D6', reviews: Math.floor(Math.random() * 5) + 5 },
    { date: 'D7', reviews: Math.floor(Math.random() * 5) + 3 },
  ];

  const ratingDistribution = [
    { rating: '5 ⭐', count: stats.r5 || 0 },
    { rating: '4 ⭐', count: stats.r4 || 0 },
    { rating: '3 ⭐', count: stats.r3 || 0 },
    { rating: '2 ⭐', count: stats.r2 || 0 },
    { rating: '1 ⭐', count: stats.r1 || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Avaliações"
        metric={stats.avg?.toFixed(1) || '0'}
        subMetrics={[
          { label: "Total", value: stats.count.toString() },
          { label: "5 Estrelas", value: `${pct5Stars.toFixed(0)}%`, trend: "up", change: 5 },
          { label: "Sem Resposta", value: `${pctNoReply.toFixed(0)}%`, trend: "down", change: 2 },
        ]}
        color={MARKETING_COLORS.Growth}
      />

      {/* Period Selector */}
      <Toolbar period={period} onPeriodChange={setPeriod} />

      {/* KPI Cards */}
      <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4">
        <StatCard
          label="Avaliações Totais"
          value={stats.count.toString()}
          icon={Star}
          trend="up"
          change={8}
        />
        <StatCard
          label="% 5 Estrelas"
          value={`${pct5Stars.toFixed(0)}%`}
          icon={TrendingUp}
          trend="up"
          change={3}
        />
        <StatCard
          label="Sem Resposta"
          value={`${pctNoReply.toFixed(0)}%`}
          icon={AlertCircle}
          trend="down"
          change={2}
        />
        <StatCard
          label="Respondidas"
          value={((stats.replied || 0) / stats.count * 100).toFixed(0) + '%'}
          icon={CheckCircle2}
          trend="up"
          change={5}
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard
          title="Distribuição de Estrelas"
          subtitle="Classificação das avaliações"
          height="300px"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ratingDistribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="rating" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="count"
                name="Avaliações"
                fill={MARKETING_COLORS.Growth}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Evolução Temporal"
          subtitle="Últimos 7 dias"
          height="300px"
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timelineData}>
              <defs>
                <linearGradient id="gradReviews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={MARKETING_COLORS.Growth} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={MARKETING_COLORS.Growth} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="reviews"
                name="Avaliações"
                stroke={MARKETING_COLORS.Growth}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Insights */}
      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard
          type={pct5Stars >= 70 ? "success" : "insight"}
          title="Satisfação Geral"
          message={pct5Stars >= 70 ? `${pct5Stars.toFixed(0)}% de 5 estrelas — Excelente satisfação!` : `${pct5Stars.toFixed(0)}% de 5 estrelas — Oportunidade de melhoria`}
        />
        <InsightCard
          type={pctNoReply < 10 ? "success" : "alert"}
          title="Taxa de Resposta"
          message={pctNoReply < 10 ? `${(100-pctNoReply).toFixed(0)}% respondidas — Bom engajamento!` : `${pctNoReply.toFixed(0)}% sem resposta — Responda logo!`}
        />
        <InsightCard
          type="insight"
          title="Ação Recomendada"
          message={stats.r1 + stats.r2 > 0 ? `${stats.r1 + stats.r2} avaliações baixas (1-2⭐) — Responda com empatia e solução` : `Continue incentivando mais avaliações!`}
        />
      </div>
      {/* Filter & Reviews List */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {[{v:"all",l:"Todas"},{v:"5",l:"⭐ 5"},{v:"4",l:"⭐ 4"},{v:"3",l:"⭐ 3"},{v:"2",l:"⭐ 2"},{v:"1",l:"⭐ 1"},{v:"no-reply",l:"Sem resposta"},{v:"hidden",l:"Ocultas"}].map(f => (
            <button key={f.v} onClick={() => setFilter(f.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${filter === f.v ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-transparent text-muted-foreground hover:text-foreground"}`}>
              {f.l}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Star className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="font-medium">Nenhuma avaliação neste filtro</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="reviews-list">
            {filtered.slice(0, 10).map(review => (
              <div key={review.id} className={`p-4 rounded-xl border transition ${review.hidden ? "bg-muted/30 opacity-60" : "bg-card border-border/50"}`} data-testid={`review-card-${review.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{review.customer_name || "Cliente"}</span>
                      <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} className={`w-3.5 h-3.5 ${i <= review.rating ? "text-amber-400 fill-amber-400" : "text-gray-200"}`} />)}</div>
                      <span className="text-[10px] text-muted-foreground">{review.created_at ? new Date(review.created_at).toLocaleDateString("pt-BR") : ""}</span>
                      {review.hidden && <Badge variant="outline" className="text-[10px] text-red-500">Oculta</Badge>}
                      {!review.admin_reply && <Badge className="text-[10px] bg-amber-100 text-amber-700">Sem resposta</Badge>}
                    </div>
                    {review.comment && <p className="text-sm text-muted-foreground mt-1.5">{review.comment}</p>}
                    {review.admin_reply && replyingId !== review.id && (
                      <div className="mt-2 pl-3 border-l-2 border-emerald-300 bg-emerald-50/50 rounded-r-lg p-2">
                        <p className="text-[10px] font-semibold text-emerald-700 mb-0.5">Sua resposta</p>
                        <p className="text-xs text-emerald-800">{review.admin_reply}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => toggleVisibility(review.id, !review.hidden)} className="p-1.5 rounded-lg hover:bg-secondary transition text-muted-foreground" title={review.hidden ? "Mostrar" : "Ocultar"}>
                      {review.hidden ? "👁️" : "👁️‍🗨️"}
                    </button>
                    <button onClick={() => { setReplyingId(replyingId === review.id ? null : review.id); setReplyText(review.admin_reply || ""); }} className="p-1.5 rounded-lg hover:bg-secondary transition text-muted-foreground" title="Responder">
                      <MessageSquare className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {replyingId === review.id && (
                  <div className="mt-3 space-y-2">
                    <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Responda com empatia e ofereça solução..." className="text-sm resize-none h-16" />
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setReplyingId(null)}>Cancelar</Button>
                      <Button size="sm" onClick={() => handleReply(review.id)} disabled={!replyText.trim()} className="gap-1">
                        <Send className="w-3 h-3" />Responder
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
